import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Box, Typography } from '@mui/material';
import SpeedIcon from "@mui/icons-material/Speed";

// Animated Speaker Component with Persistent Sound Waves
const AnimatedSpeakerIcon = ({ isPlaying }) => {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="64" 
      height="64" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="white" 
      strokeWidth="2"
    >
      {/* Speaker Body */}
      <path d="M12 6 L7 10 L3 10 L3 14 L7 14 L12 18 Z" fill="white" />
      
      {/* Sound Waves - Always present, animated only when playing */}
      <path 
        d="M15 8 Q17 10, 17 12 Q17 14, 15 16" 
        strokeLinecap="round" 
        fill="none" 
        opacity={isPlaying ? 1 : 0.3}
        style={{ 
          animation: isPlaying ? 'wave1 1s infinite' : 'none',
          transformOrigin: 'center'
        }} 
      />
      <path 
        d="M17 6 Q20 10, 20 12 Q20 15, 17 18" 
        strokeLinecap="round" 
        fill="none" 
        opacity={isPlaying ? 1 : 0.3}
        style={{ 
          animation: isPlaying ? 'wave2 1s infinite 0.3s' : 'none',
          transformOrigin: 'center'
        }} 
      />
    </svg>
  );
};

// AudioComponent accepts both audioData (base64) and audioCdnUrl (direct URL)
const AudioComponent = ({ audioData, audioCdnUrl }) => {
    const audioRef = useRef(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [playbackSpeed, setPlaybackSpeed] = useState(1);
    const [audioSrc, setAudioSrc] = useState("");
    const [hasAudio, setHasAudio] = useState(false);

    // Playback speed options
    const speeds = [0.5, 0.75, 1];

    // Set the audio source - prioritize CDN URL if available
    useEffect(() => {
        if (audioCdnUrl) {
            setAudioSrc(audioCdnUrl);
            setHasAudio(true);
        } else if (audioData) {
            setAudioSrc(audioData);
            setHasAudio(true);
        } else {
            setHasAudio(false);
        }
    }, [audioCdnUrl, audioData]);

    // Memoize the handleSpeakerClick function to maintain consistent references
    const handleSpeakerClick = useCallback(() => {
        if (!audioRef.current || !hasAudio) return;
        
        if (!isPlaying) {
            // Play audio
            const playPromise = audioRef.current.play();
            
            // Modern browsers return a promise from play()
            if (playPromise !== undefined) {
                playPromise
                    .then(() => {
                        setIsPlaying(true);
                        console.log("Audio playback started successfully");
                    })
                    .catch(error => {
                        console.error("Error playing audio:", error);
                        setIsPlaying(false);
                    });
            } else {
                // Fallback for older browsers
                setIsPlaying(true);
            }
        } else {
            // Pause audio
            audioRef.current.pause();
            setIsPlaying(false);
        }
    }, [isPlaying, hasAudio]);

    // Memoize speed change function for consistent references
    const handleSpeedChange = useCallback(() => {
        if (!audioRef.current || !hasAudio) return;
        
        const currentIndex = speeds.indexOf(playbackSpeed);
        const nextSpeed = speeds[(currentIndex + 1) % speeds.length];

        audioRef.current.playbackRate = nextSpeed;
        setPlaybackSpeed(nextSpeed);
    }, [playbackSpeed, speeds, hasAudio]);

    // Setup event listeners for audio element
    useEffect(() => {
        const audioElement = audioRef.current;
        if (!audioElement) return;
        
        // Define event handlers
        const handleAudioEnd = () => {
            console.log("Audio ended event fired");
            setIsPlaying(false);
        };
        
        const handleAudioPause = () => {
            console.log("Audio paused event fired");
            setIsPlaying(false);
        };
        
        const handleAudioPlay = () => {
            console.log("Audio play event fired");
            setIsPlaying(true);
        };

        // Add event listeners to audio element
        audioElement.addEventListener('ended', handleAudioEnd);
        audioElement.addEventListener('pause', handleAudioPause);
        audioElement.addEventListener('playing', handleAudioPlay);
        
        // Clean up listeners on unmount or source change
        return () => {
            audioElement.removeEventListener('ended', handleAudioEnd);
            audioElement.removeEventListener('pause', handleAudioPause);
            audioElement.removeEventListener('playing', handleAudioPlay);
        };
    }, [audioSrc]); // Re-run when audio source changes

    // Global keyboard listener for play/pause
    useEffect(() => {
        const handleKeyPress = (event) => {
            // Hotkey 'p' to play/pause audio
            if ((event.key === 'p' || event.key === 'P') && hasAudio) {
                // Prevent triggering if in text input
                if (
                    document.activeElement.tagName === 'INPUT' ||
                    document.activeElement.tagName === 'TEXTAREA'
                ) {
                    return;
                }
                event.preventDefault();
                handleSpeakerClick();
            }
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => {
            window.removeEventListener('keydown', handleKeyPress);
        };
    }, [handleSpeakerClick, hasAudio]); // Include handleSpeakerClick in dependencies

    // If no audio source is available, don't render
    if (!hasAudio) return null;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1,
                position: 'absolute',
                top: '50%',
                transform: 'translateY(-50%)',
                padding: "0% 1% 0% 1%",
                borderRadius: '10%',
            }}
        >
            <style>
                {`
                @keyframes wave1 {
                    0%, 100% { opacity: 0.5; transform: scaleX(0.8); }
                    50% { opacity: 1; transform: scaleX(1); }
                }
                @keyframes wave2 {
                    0%, 100% { opacity: 0.5; transform: scaleX(0.8); }
                    50% { opacity: 1; transform: scaleX(1); }
                }
                `}
            </style>

            <audio
                ref={audioRef}
                src={audioSrc}
                preload="auto"
                onError={(e) => console.error("Audio error:", e)}
                style={{ display: 'none' }}
            />

            <Box
                sx={{
                    borderRadius: '50%',
                    p: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'background-color 0.3s',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)'
                    },
                }}
                onClick={handleSpeakerClick}
                data-testid="audio-speaker-button"
            >
                <AnimatedSpeakerIcon isPlaying={isPlaying} />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    cursor: 'pointer',
                    '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.2)'
                    }, 
                    px: 1,
                    py: 0.5,
                    mt: -2
                }}
                onClick={handleSpeedChange}
                data-testid="audio-speed-button"
            >
                <SpeedIcon
                    sx={{
                        color: 'white',
                        fontSize: '1rem',
                        mr: 0.5,
                    }}
                />
                <Typography
                    variant="caption"
                    sx={{
                        color: 'white',
                        fontWeight: 'bold'
                    }}
                >
                    {playbackSpeed}x
                </Typography>
            </Box>
        </Box>
    );
};

export default AudioComponent;